import { useEffect, useState } from 'react';
import type { CostItem, DocCategory, Document } from 'used-vehicle-contracts';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { toOptionalText } from '../estimates/estimates-display';
import { DOC_CATEGORIES } from './documents-display';

export interface DocumentMetaSubmission {
  id: string;
  category: DocCategory;
  title: string;
  documentDate: string | null;
  sourceProvider: string | null;
  expiryDate: string | null;
  notes: string | null;
  costItemId: string | null;
}

export interface DocumentMetaSheetProps {
  isOpen: boolean;
  document: Document | null;
  /** The vehicle's cost items, so an invoice can be tied to the work it evidences (PP §8). */
  costItems: readonly CostItem[];
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (submission: DocumentMetaSubmission) => void;
}

interface FormState {
  category: DocCategory;
  title: string;
  documentDate: string;
  sourceProvider: string;
  expiryDate: string;
  notes: string;
  costItemId: string;
}

const fromDocument = (document: Document | null): FormState => ({
  category: document?.category ?? 'OTHER',
  title: document?.title ?? '',
  documentDate: document?.documentDate ?? '',
  sourceProvider: document?.sourceProvider ?? '',
  expiryDate: document?.expiryDate ?? '',
  notes: document?.notes ?? '',
  costItemId: document?.costItemId ?? '',
});

/**
 * The metadata of one document (PP §8: category, title, document date, source/provider, expiry, related
 * work item, notes).
 *
 * There is no file field: the **bytes are immutable**. Correcting a document is "Replace", which keeps
 * both rows and both files, so a record never quietly changes underneath a decision that was taken on it.
 *
 * `expiryDate` is on this form rather than on the image because a licence expiry has to stay legible
 * even when an older scan is the one on file (PP §8).
 */
export const DocumentMetaSheet = ({ isOpen, document, costItems, isSaving, error, onClose, onSubmit }: DocumentMetaSheetProps) => {
  const { t } = useI18nTranslation();
  const [form, setForm] = useState<FormState>(() => fromDocument(document));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(fromDocument(document));
      setTouched(false);
    }
  }, [isOpen, document]);

  const change = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));
  const titleMissing = form.title.trim() === '';

  const submit = () => {
    setTouched(true);

    if (titleMissing || document === null) {
      return;
    }

    onSubmit({
      id: document.id,
      category: form.category,
      title: form.title.trim(),
      documentDate: toOptionalText(form.documentDate),
      sourceProvider: toOptionalText(form.sourceProvider),
      expiryDate: toOptionalText(form.expiryDate),
      notes: toOptionalText(form.notes),
      costItemId: form.costItemId === '' ? null : form.costItemId,
    });
  };

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('documents.metaTitle')}
      description={t('documents.metaBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={isSaving}
            onClick={submit}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <FormField
          id="document-title"
          label={t('documents.title')}
          error={touched && titleMissing ? t('documents.titleRequired') : undefined}
        >
          <Input
            id="document-title"
            value={form.title}
            hasError={touched && titleMissing}
            onChange={(event) => change({ title: event.target.value })}
          />
        </FormField>

        <FormField
          id="document-category"
          label={t('documents.category')}
          hint={t('documents.categoryHint')}
        >
          <Select
            id="document-category"
            value={form.category}
            onChange={(event) => change({ category: event.target.value as DocCategory })}
            options={DOC_CATEGORIES.map((category) => ({ value: category, label: t(`enums.docCategory.${category}`) }))}
          />
        </FormField>

        <FormField
          id="document-date"
          label={t('documents.documentDate')}
          hint={t('documents.documentDateHint')}
        >
          <Input
            id="document-date"
            type="date"
            value={form.documentDate}
            onChange={(event) => change({ documentDate: event.target.value })}
          />
        </FormField>

        <FormField
          id="document-provider"
          label={t('documents.sourceProvider')}
        >
          <Input
            id="document-provider"
            value={form.sourceProvider}
            onChange={(event) => change({ sourceProvider: event.target.value })}
          />
        </FormField>

        <FormField
          id="document-expiry"
          label={t('documents.expiryDate')}
          hint={t('documents.expiryHint')}
        >
          <Input
            id="document-expiry"
            type="date"
            value={form.expiryDate}
            onChange={(event) => change({ expiryDate: event.target.value })}
          />
        </FormField>

        <FormField
          id="document-cost-item"
          label={t('documents.linkedCostItem')}
          hint={t('documents.linkedCostItemHint')}
        >
          <Select
            id="document-cost-item"
            value={form.costItemId}
            placeholder={t('documents.noCostItem')}
            onChange={(event) => change({ costItemId: event.target.value })}
            options={costItems.map((item) => ({ value: item.id, label: item.name }))}
          />
        </FormField>

        <FormField
          id="document-notes"
          label={t('documents.notes')}
        >
          <Textarea
            id="document-notes"
            rows={3}
            value={form.notes}
            onChange={(event) => change({ notes: event.target.value })}
          />
        </FormField>
      </div>
    </Sheet>
  );
};
